/**
 * batch-69 km-balans (FO-515, spec 215) - de werk- en privekilometerbalans per
 * medewerker per maand.
 *
 * Een INDICATIE, geen bewijs en geen rittenregistratie. De totaalkilometers van
 * een voertuig zijn GEMETEN (kilometerstanden rond de maandgrenzen) of GESCHAT
 * (getankte liters of geladen kWh maal een verbruiksfactor); elke waarde draagt
 * die bron. De werkkilometers komen uit de bestaande reisritten van de
 * werkbonnen (zelfde regel als de uitbetaalde reis). Het verschil is de
 * privé-indicatie. De app trekt daar geen conclusie uit en leidt er geen
 * bijtellingsbesluit uit af: een uitkomst boven de ingestelde drempel is een
 * aanleiding voor een gesprek, niets meer.
 *
 * Geen bedragen: de balans rekent in kilometers en liters. Het portal-DTO
 * (`MijnKmBalansDTO`) bevat bovendien geen drempel- of signaalvelden.
 */

import { z } from 'zod'
import { idParam } from './id-validators'
import { periodeLabelSchema } from './kostprijs-werkelijk'
import { queryBoolean } from './zod-query'
import { VOERTUIG_BRANDSTOF, VOERTUIG_TYPE, type VoertuigBrandstof, type VoertuigType } from './voertuigen'
import type { VoertuigGebruikType } from './voertuig-verklaring'

// ─── Bron, zekerheid, drager ─────────────────────────────────────────────────

/**
 * Waar komt het totaal van de maand vandaan, van betrouwbaar naar minder
 * betrouwbaar. Een meting wint ALTIJD van een schatting.
 */
export const KM_BALANS_BRONNEN = ['GEMETEN', 'GESCHAT_VOERTUIG', 'GESCHAT_TYPE', 'GESCHAT_INGESTELD', 'GESCHAT_STANDAARD', 'GEEN'] as const
export type KmBalansBron = (typeof KM_BALANS_BRONNEN)[number]

export const KM_BALANS_BRON_LABELS: Record<KmBalansBron, string> = {
  GEMETEN: 'Gemeten',
  GESCHAT_VOERTUIG: 'Geschat (gemeten verbruik van dit voertuig)',
  GESCHAT_TYPE: 'Geschat (gemiddelde van het voertuigtype)',
  GESCHAT_INGESTELD: 'Geschat (ingestelde factor)',
  GESCHAT_STANDAARD: 'Geschat (standaardverbruik)',
  GEEN: 'Geen meting',
}

/** Korte badge-tekst per bron. */
export const KM_BALANS_BRON_BADGE: Record<KmBalansBron, string> = {
  GEMETEN: 'Gemeten',
  GESCHAT_VOERTUIG: 'Geschat',
  GESCHAT_TYPE: 'Geschat',
  GESCHAT_INGESTELD: 'Geschat',
  GESCHAT_STANDAARD: 'Geschat',
  GEEN: 'Geen meting',
}

/** Uitleg per bron in gewone taal (tooltip, export-info, portal). */
export const KM_BALANS_BRON_UITLEG: Record<KmBalansBron, string> = {
  GEMETEN:
    'Uit de kilometerstanden op of vlak bij de maandgrenzen (tankbon, doorgegeven stand of garage); in de lopende maand tot en met vandaag.',
  GESCHAT_VOERTUIG: 'Getankte liters maal het gemeten verbruik van dit voertuig (laatste 6 maanden).',
  GESCHAT_TYPE:
    'Getankte liters maal het gemiddelde verbruik van voertuigen van hetzelfde type en dezelfde brandstof, met een bandbreedte.',
  GESCHAT_INGESTELD:
    'Getankte liters (of geladen kWh) maal een verbruiksfactor die voor dit voertuig of dit voertuigtype is ingesteld: een ingestelde waarde, geen meting, dus met een bandbreedte.',
  GESCHAT_STANDAARD:
    'Getankte liters (of geladen kWh) maal het standaardverbruik van de brandstofsoort, met een bandbreedte. Dit is de grofste schatting.',
  GEEN: 'Geen kilometerstanden en geen tankbeurten in deze maand: het totaal is onbekend.',
}

/** Van sterk naar zwak; de zwakste bron van een maand bepaalt het label. */
export const KM_BALANS_BRON_RANG: Record<KmBalansBron, number> = {
  GEMETEN: 0,
  GESCHAT_VOERTUIG: 1,
  GESCHAT_TYPE: 2,
  GESCHAT_INGESTELD: 3,
  GESCHAT_STANDAARD: 4,
  GEEN: 5,
}

export const KM_BALANS_ZEKERHEDEN = ['HOOG', 'MIDDEL', 'LAAG'] as const
export type KmBalansZekerheid = (typeof KM_BALANS_ZEKERHEDEN)[number]

export const KM_BALANS_ZEKERHEID_LABELS: Record<KmBalansZekerheid, string> = {
  HOOG: 'Hoge zekerheid',
  MIDDEL: 'Redelijke zekerheid',
  LAAG: 'Lage zekerheid',
}

/** Energiedrager van een tankbeurt: liters brandstof of kWh laden. */
export const KM_ENERGIE_DRAGERS = ['LITER', 'KWH'] as const
export type KmEnergieDrager = (typeof KM_ENERGIE_DRAGERS)[number]

export const KM_ENERGIE_DRAGER_LABELS: Record<KmEnergieDrager, string> = {
  LITER: 'Liter brandstof',
  KWH: 'kWh laden',
}

/** Op welk niveau een verbruiksfactor is ingesteld. */
export const KM_VERBRUIKSFACTOR_NIVEAUS = ['STANDAARD', 'TYPE', 'VOERTUIG'] as const
export type KmVerbruiksfactorNiveau = (typeof KM_VERBRUIKSFACTOR_NIVEAUS)[number]

export const KM_VERBRUIKSFACTOR_NIVEAU_LABELS: Record<KmVerbruiksfactorNiveau, string> = {
  STANDAARD: 'Standaard per brandstofsoort',
  TYPE: 'Per voertuigtype',
  VOERTUIG: 'Per voertuig',
}

/**
 * Waar de gebruikte verbruiksfactor vandaan kwam (alleen bij een schatting).
 * Volgorde = voorrang: het gemeten verbruik van het voertuig zelf wint van elke
 * ingestelde factor; een ingestelde factor wint van het typegemiddelde, enzovoort.
 */
export const KM_FACTOR_HERKOMSTEN = [
  'VOERTUIG_GEMETEN',
  'VOERTUIG_INGESTELD',
  'TYPE_GEMETEN',
  'TYPE_INGESTELD',
  'STANDAARD_INGESTELD',
  'STANDAARD',
] as const
export type KmFactorHerkomst = (typeof KM_FACTOR_HERKOMSTEN)[number]

export const KM_FACTOR_HERKOMST_LABELS: Record<KmFactorHerkomst, string> = {
  VOERTUIG_GEMETEN: 'gemeten verbruik van dit voertuig (laatste 6 maanden)',
  VOERTUIG_INGESTELD: 'ingesteld voor dit voertuig',
  TYPE_GEMETEN: 'gemeten gemiddelde van dit voertuigtype',
  TYPE_INGESTELD: 'ingesteld voor dit voertuigtype',
  STANDAARD_INGESTELD: 'ingesteld standaardverbruik',
  STANDAARD: 'standaardverbruik',
}

/**
 * De bron die een factor-herkomst oplevert. Alleen het GEMETEN verbruik van het
 * voertuig zelf heet "verbruik van dit voertuig"; een ingestelde voertuig- of
 * typefactor is een ingestelde waarde, geen meting (eigen bron, lage zekerheid).
 */
export const KM_FACTOR_HERKOMST_BRON: Record<KmFactorHerkomst, KmBalansBron> = {
  VOERTUIG_GEMETEN: 'GESCHAT_VOERTUIG',
  VOERTUIG_INGESTELD: 'GESCHAT_INGESTELD',
  TYPE_GEMETEN: 'GESCHAT_TYPE',
  TYPE_INGESTELD: 'GESCHAT_INGESTELD',
  STANDAARD_INGESTELD: 'GESCHAT_STANDAARD',
  STANDAARD: 'GESCHAT_STANDAARD',
}

/**
 * Alleen het eigen gemeten verbruik is nauwkeurig genoeg om zonder bandbreedte te
 * rekenen; elke andere factor (typegemiddelde, ingesteld of standaard) krijgt de
 * ingestelde bandbreedte. Op de herkomst, niet op de bron.
 */
export const KM_FACTOR_HERKOMST_ZONDER_BANDBREEDTE: readonly KmFactorHerkomst[] = ['VOERTUIG_GEMETEN']

export const KM_BALANS_STATUSSEN = ['OPEN', 'AFGESLOTEN'] as const
export type KmBalansStatus = (typeof KM_BALANS_STATUSSEN)[number]

export const KM_BALANS_STATUS_LABELS: Record<KmBalansStatus, string> = {
  OPEN: 'Open (wordt elke nacht bijgewerkt)',
  AFGESLOTEN: 'Afgesloten',
}

/** Markeringen op een maand: zichtbaar, nooit stil. */
export const KM_BALANS_MARKERINGEN = [
  'WERK_BOVEN_TOTAAL',
  'WERK_ONVOLLEDIG',
  'WISSELDAG_GELIJK_VERDEELD',
  'DAGEN_ZONDER_BESTUURDER',
  'KM_STANDEN_GENEGEERD',
  'WEINIG_WERKDAGEN',
  'VOERTUIG_ZONDER_METING',
  'WERK_ZONDER_BEDRIJFSAUTO',
  'WOON_WERK_NIET_ALS_WERK',
  'GEEN_WERKDAGEN',
] as const
export type KmBalansMarkering = (typeof KM_BALANS_MARKERINGEN)[number]

export const KM_BALANS_MARKERING_LABELS: Record<KmBalansMarkering, string> = {
  WERK_BOVEN_TOTAAL:
    'De werkkilometers zijn hoger dan het totaal van de maand. Controleer de verbruiksfactor, de kilometerstanden of de reiskilometers.',
  WERK_ONVOLLEDIG:
    'Voor een of meer werkdagen ontbreekt de reisberekening, of heeft een rit geen afstand (een adres zonder coördinaten): de werkkilometers zijn onvolledig. Telt niet mee voor een signaal.',
  WISSELDAG_GELIJK_VERDEELD:
    'Op een wisseldag hadden meerdere bestuurders deze auto en was niet eenduidig wie die dag werkte (geen of meer dan een werkdag): die dag is gelijk verdeeld.',
  DAGEN_ZONDER_BESTUURDER:
    'Het voertuig had in deze maand ook dagen zonder bestuurder: dat deel van de kilometers is naar rato van de dagen aan niemand toegerekend.',
  KM_STANDEN_GENEGEERD:
    'Een of meer kilometerstanden pasten niet in de reeks (bijvoorbeeld lager dan de vorige) en zijn genegeerd.',
  WEINIG_WERKDAGEN:
    'Weinig werkdagen op werkbonnen in deze maand (bijvoorbeeld verlof of ziekte): de privé-indicatie kan daardoor hoog uitvallen. Telt niet mee voor een melding en staat in de tellers apart.',
  GEEN_WERKDAGEN:
    'Geen werkdagen op werkbonnen in deze maand (bijvoorbeeld verlof, of iemand die niet op werkbonnen werkt): de werkkilometers zijn onbekend, er is niet vergeleken met de drempel.',
  VOERTUIG_ZONDER_METING:
    'Op een voertuig zonder meting of tankbeurten in deze maand: die werkkilometers staan apart en tellen niet mee in de vergelijking.',
  WERK_ZONDER_BEDRIJFSAUTO: 'Werkkilometers op dagen zonder bedrijfsauto staan apart en tellen niet mee in de vergelijking.',
  WOON_WERK_NIET_ALS_WERK: 'Woon-werkritten tellen volgens de instelling niet als werk.',
}

/** Soort reisrit (RouteType) in gewone taal, voor de drill-down. */
export const KM_ROUTE_LABELS: Record<string, string> = {
  WOON_WERK: 'Woon-werk',
  WERK_WERK: 'Tussen werklocaties',
  WERK_WOON: 'Werk-woon',
  HEEN: 'Heen',
  TERUG: 'Terug',
}

/** Vaste uitleg die op elk scherm met de balans staat. */
export const KM_BALANS_DISCLAIMER =
  'Dit is een indicatie, geen bewijs en geen rittenregistratie. Elke waarde toont of hij gemeten of geschat is. ' +
  'Gebruik de uitkomst hooguit als aanleiding voor een gesprek; de app trekt er geen conclusie uit.'

/** Uitleg voor de medewerker zelf (portal). */
export const KM_BALANS_PORTAL_UITLEG =
  'Dit is een schatting, geen rittenregistratie. Het totaal komt uit je kilometerstanden of, als die ontbreken, ' +
  'uit de getankte liters maal een gemiddeld verbruik. De werkkilometers komen uit de reisritten van je werkbonnen. ' +
  'Geef bij het tanken of aan het begin van de maand je kilometerstand door: dan wordt het totaal gemeten in plaats van geschat.'

// ─── Standaardwaarden ────────────────────────────────────────────────────────

/**
 * Standaardwaarden. De instelbare waarden staan in `WagenparkInstelling`
 * (prefix `wagenpark.kmBalans.*`) en de verbruiksfactoren in
 * `KmVerbruiksfactor`; ontbreekt een rij, dan geldt deze waarde.
 */
export const KM_BALANS_DEFAULTS = {
  /** Standaardverbruik brandstof: 1 liter op 13 km (= 7,7 l/100 km). */
  standaardKmPerLiter: 13,
  /** Standaardverbruik elektrisch: 1 kWh op 5 km (= 20 kWh/100 km). */
  standaardKmPerKwh: 5,
  /** Woon-werkritten tellen als werk (zoals bij de bijtelling). */
  woonWerkTeltAlsWerk: true,
  /** Drempel voor de vergelijking met "uitsluitend zakelijk": privé-indicatie boven dit percentage ... */
  drempelPrivePct: 10,
  /** ... of boven dit aantal kilometers per maand. */
  drempelPriveKm: 500,
  /** Zoveel maanden op rij boven de drempel = structureel (signaal). */
  structureelMaanden: 3,
  /** Minder werkdagen dan dit = "weinig werkdagen" (telt niet mee voor een signaal). */
  minWerkdagen: 10,
  /**
   * Bandbreedte (plus of min) bij een schatting zonder eigen gemeten verbruik: met
   * het typegemiddelde, een ingestelde factor of het standaardverbruik.
   */
  bandbreedteStandaardPct: 20,
  /** Portal-hint "geef je kilometerstand door" na zoveel maanden zonder stand. */
  geenKmStandMaanden: 2,
  // ── Vaste rekenregels (geen instelling) ──
  /**
   * Zonder stand aan beide kanten van de maandgrens: een stand binnen zoveel dagen
   * van de grens, doorgetrokken met het dagtempo van de gemeten standen. Verder weg
   * is het geen meting meer van de hele maand (dan volgt een schatting).
   */
  meetNabijDagen: 3,
  /** Lineair interpoleren tussen twee standen die hooguit zoveel dagen uit elkaar liggen. */
  meetMaxIntervalDagen: 45,
  /** Een meting met een stand binnen zoveel dagen van beide grenzen heeft hoge zekerheid. */
  meetHoogDagen: 3,
  /**
   * Meer dan zoveel kilometers per dag tussen twee standen is niet mogelijk: een van
   * beide is een typefout (een cijfer te veel of te weinig) en wordt genegeerd.
   * Standen binnen een dag tellen als een dag. Ruim genoeg voor een lange rit op een
   * dag (1.100 km); een cijfer te veel (een sprong maal tien) valt er altijd buiten.
   */
  meetMaxKmPerDag: 1500,
  /** Het gemeten verbruik van een voertuig over zoveel maanden (inclusief de maand zelf). */
  gemetenFactorMaanden: 6,
  /** Minimaal zoveel gemeten kilometers voor een eigen voertuigverbruik. */
  gemetenMinKmVoertuig: 500,
  /** Minimaal zoveel gemeten kilometers en voertuigen voor een typegemiddelde. */
  gemetenMinKmType: 1000,
  gemetenMinVoertuigenType: 2,
  /** Alleen een gemeten verbruik tussen deze grenzen (l/100 km) telt als factor. */
  verbruikPlausibelMin: 3,
  verbruikPlausibelMax: 30,
} as const

/** De instelbare waarden (zonder de verbruiksfactoren). */
export interface KmBalansInstellingen {
  woonWerkTeltAlsWerk: boolean
  drempelPrivePct: number
  drempelPriveKm: number
  structureelMaanden: number
  minWerkdagen: number
  bandbreedteStandaardPct: number
  geenKmStandMaanden: number
}

export const KM_BALANS_INSTELLINGEN_DEFAULTS: KmBalansInstellingen = {
  woonWerkTeltAlsWerk: KM_BALANS_DEFAULTS.woonWerkTeltAlsWerk,
  drempelPrivePct: KM_BALANS_DEFAULTS.drempelPrivePct,
  drempelPriveKm: KM_BALANS_DEFAULTS.drempelPriveKm,
  structureelMaanden: KM_BALANS_DEFAULTS.structureelMaanden,
  minWerkdagen: KM_BALANS_DEFAULTS.minWerkdagen,
  bandbreedteStandaardPct: KM_BALANS_DEFAULTS.bandbreedteStandaardPct,
  geenKmStandMaanden: KM_BALANS_DEFAULTS.geenKmStandMaanden,
}

export const kmBalansInstellingenSchema = z.object({
  woonWerkTeltAlsWerk: z.boolean(),
  drempelPrivePct: z
    .number({ error: 'Vul een percentage in.' })
    .min(0, 'Minimaal 0%.')
    .max(100, 'Maximaal 100%.'),
  drempelPriveKm: z
    .number({ error: 'Vul een aantal kilometers in.' })
    .int('Gebruik hele kilometers.')
    .min(0, 'Minimaal 0 km.')
    .max(20_000, 'Maximaal 20.000 km.'),
  structureelMaanden: z
    .number({ error: 'Vul een aantal maanden in.' })
    .int('Gebruik hele maanden.')
    .min(1, 'Minimaal 1 maand.')
    .max(12, 'Maximaal 12 maanden.'),
  minWerkdagen: z
    .number({ error: 'Vul een aantal werkdagen in.' })
    .int('Gebruik hele dagen.')
    .min(0, 'Minimaal 0 dagen.')
    .max(31, 'Maximaal 31 dagen.'),
  bandbreedteStandaardPct: z
    .number({ error: 'Vul een percentage in.' })
    .int('Gebruik hele procenten.')
    .min(0, 'Minimaal 0%.')
    .max(100, 'Maximaal 100%.'),
  geenKmStandMaanden: z
    .number({ error: 'Vul een aantal maanden in.' })
    .int('Gebruik hele maanden.')
    .min(1, 'Minimaal 1 maand.')
    .max(12, 'Maximaal 12 maanden.'),
})
export type KmBalansInstellingenInvoer = z.infer<typeof kmBalansInstellingenSchema>

// ─── Brandstof -> energiedrager ──────────────────────────────────────────────

/** Welke dragers een brandstofsoort tankt of laadt (een hybride kan beide). */
export const KM_DRAGERS_PER_BRANDSTOF: Record<VoertuigBrandstof, readonly KmEnergieDrager[]> = {
  BENZINE: ['LITER'],
  DIESEL: ['LITER'],
  ELEKTRISCH: ['KWH'],
  HYBRIDE: ['LITER', 'KWH'],
  HYBRIDE_BENZINE: ['LITER', 'KWH'],
  HYBRIDE_DIESEL: ['LITER', 'KWH'],
  LPG: ['LITER'],
  WATERSTOF: ['LITER'],
  OVERIG: ['LITER', 'KWH'],
}

/** Het ingebouwde standaardverbruik (km per eenheid) per drager. */
export function standaardKmPerEenheid(drager: KmEnergieDrager): number {
  return drager === 'KWH' ? KM_BALANS_DEFAULTS.standaardKmPerKwh : KM_BALANS_DEFAULTS.standaardKmPerLiter
}

/** "1 op 13" (liter) of "1 kWh op 5 km" (kWh), NL-notatie. */
export function formatVerbruiksfactor(kmPerEenheid: number, drager: KmEnergieDrager): string {
  const n = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(kmPerEenheid)
  return drager === 'KWH' ? `1 kWh op ${n} km` : `1 op ${n}`
}

/** De factor als verbruik per 100 km ("7,7 l/100 km" of "20 kWh/100 km"). */
export function formatVerbruikPer100(kmPerEenheid: number, drager: KmEnergieDrager): string {
  const per100 = kmPerEenheid > 0 ? 100 / kmPerEenheid : 0
  const n = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(per100)
  return drager === 'KWH' ? `${n} kWh/100 km` : `${n} l/100 km`
}

// ─── Verbruiksfactor instellen ───────────────────────────────────────────────

export const kmVerbruiksfactorOpslaanSchema = z
  .object({
    niveau: z.enum(KM_VERBRUIKSFACTOR_NIVEAUS),
    drager: z.enum(KM_ENERGIE_DRAGERS),
    brandstof: z.enum(VOERTUIG_BRANDSTOF).nullable().optional(),
    voertuigType: z.enum(VOERTUIG_TYPE).nullable().optional(),
    voertuigId: idParam('voertuigId').nullable().optional(),
    /** Kilometers per liter (1 op X) of per kWh. */
    kmPerEenheid: z
      .number({ error: 'Vul het aantal kilometers per eenheid in.' })
      .gt(0, 'Moet groter zijn dan 0.')
      .max(200, 'Maximaal 200 km per eenheid.')
      .refine((v) => Math.abs(Math.round(v * 1000) - v * 1000) < 1e-6, {
        message: 'Gebruik hooguit drie decimalen.',
      }),
    toelichting: z.string().trim().max(500, 'Maximaal 500 tekens.').nullable().optional(),
  })
  .superRefine((v, ctx) => {
    // Zod 4 draait deze refine ook als een veldcheck al faalde: lees de waarden defensief.
    if (v.niveau === 'STANDAARD' || v.niveau === 'TYPE') {
      const dragers = v.brandstof ? KM_DRAGERS_PER_BRANDSTOF[v.brandstof] : undefined
      if (!v.brandstof) ctx.addIssue({ code: 'custom', path: ['brandstof'], message: 'Kies een brandstofsoort.' })
      else if (dragers && !dragers.includes(v.drager)) {
        ctx.addIssue({
          code: 'custom',
          path: ['drager'],
          message: v.drager === 'KWH' ? 'Deze brandstofsoort laadt geen kWh.' : 'Deze brandstofsoort tankt geen liters.',
        })
      }
    }
    if (v.niveau === 'TYPE' && !v.voertuigType) {
      ctx.addIssue({ code: 'custom', path: ['voertuigType'], message: 'Kies een voertuigtype.' })
    }
    if (v.niveau === 'VOERTUIG' && !v.voertuigId) {
      ctx.addIssue({ code: 'custom', path: ['voertuigId'], message: 'Kies een voertuig.' })
    }
  })
export type KmVerbruiksfactorOpslaanInvoer = z.infer<typeof kmVerbruiksfactorOpslaanSchema>

export const kmVerbruiksfactorVerwijderenSchema = z.object({
  reden: z.string().trim().max(500, 'Maximaal 500 tekens.').nullable().optional(),
})

export interface KmVerbruiksfactorDTO {
  id: string
  niveau: KmVerbruiksfactorNiveau
  drager: KmEnergieDrager
  brandstof: VoertuigBrandstof | null
  voertuigType: VoertuigType | null
  voertuigId: string | null
  kenteken: string | null
  kmPerEenheid: number
  /** "1 op 13" / "1 kWh op 5 km". */
  weergave: string
  /** "7,7 l/100 km". */
  per100: string
  toelichting: string | null
  gewijzigdOp: string
  gewijzigdDoor: string | null
}

/** Het standaardverbruik per brandstofsoort en drager: ingesteld of ingebouwd. */
export interface KmStandaardFactorDTO {
  brandstof: VoertuigBrandstof
  drager: KmEnergieDrager
  kmPerEenheid: number
  weergave: string
  per100: string
  /** De ingestelde rij, of null = de ingebouwde standaard (1 op 13 / 1 kWh op 5 km). */
  ingesteld: KmVerbruiksfactorDTO | null
}

/** Per voertuig: gemeten verbruik, ingestelde factor en de factor die nu zou gelden. */
export interface KmVoertuigFactorRijDTO {
  voertuigId: string
  kenteken: string
  merkModel: string
  type: VoertuigType
  brandstof: VoertuigBrandstof
  /** Gemeten verbruik (km per liter) over de laatste 6 maanden, of null. */
  gemetenKmPerLiter: number | null
  gemetenMaanden: number
  gemetenKm: number
  /** De ingestelde factoren van dit voertuig (per drager). */
  ingesteld: KmVerbruiksfactorDTO[]
  /** De factor die een schatting deze maand zou gebruiken, per drager. */
  effectief: Array<{ drager: KmEnergieDrager; kmPerEenheid: number; weergave: string; herkomst: KmFactorHerkomst; herkomstLabel: string }>
}

export interface KmVerbruiksfactorenDTO {
  standaard: KmStandaardFactorDTO[]
  type: KmVerbruiksfactorDTO[]
  voertuigen: KmVoertuigFactorRijDTO[]
  meta: { total: number; page: number; pageSize: number; magWijzigen: boolean }
}

export const kmVerbruiksfactorenQuerySchema = z.object({
  zoek: z.string().trim().max(40).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type KmVerbruiksfactorenQuery = z.infer<typeof kmVerbruiksfactorenQuerySchema>

// ─── Opbouw (eigen schrijfvorm van het maandrecord) ──────────────────────────

/** Hoe een maandgrens aan een kilometerstand is gekoppeld. */
export const KM_ANKER_METHODEN = ['INTERPOLATIE', 'NABIJ'] as const
export type KmAnkerMethode = (typeof KM_ANKER_METHODEN)[number]

export interface KmAnkerDTO {
  /** De (berekende) stand op de grens. */
  km: number
  methode: KmAnkerMethode
  /** Afstand in dagen van de grens tot de dichtstbijzijnde echte stand. */
  afstandDagen: number
  /** De gebruikte echte standen (moment ISO + stand). */
  standen: Array<{ op: string; kmStand: number }>
}

export interface KmVoertuigFactorGebruikDTO {
  drager: KmEnergieDrager
  hoeveelheid: number
  kmPerEenheid: number
  herkomst: KmFactorHerkomst
  km: number
}

/** De km-opbouw van een voertuig-maand (ook op `KostprijsWerkelijkVoertuigPeriode.kmOpbouw`). */
export interface KmVoertuigOpbouwDTO {
  bron: KmBalansBron
  km: number | null
  zekerheid: KmBalansZekerheid | null
  bandbreedtePct: number | null
  begin: KmAnkerDTO | null
  eind: KmAnkerDTO | null
  liters: number
  kwh: number
  /** Waarvan uit tankpas-transacties die (nog) niet op een factuur staan. */
  litersZonderFactuur: number
  kwhZonderFactuur: number
  factoren: KmVoertuigFactorGebruikDTO[]
  genegeerdeStanden: number
  /**
   * De lopende maand: gemeten of geschat tot en met dit moment (ISO), nooit
   * doorgetrokken voorbij "nu". Null bij een voorbije maand.
   */
  lopendTot: string | null
  /**
   * batch-70 wagenpark-week (FO-528) - waaruit de meting kwam: TELEMATICA (alleen de
   * telematica- en importstanden: de hoogste betrouwbaarheid) of STANDEN (alle
   * doorgegeven standen, tankbonnen en tankpas). Null bij een schatting of een oude rij.
   */
  meetBron?: 'TELEMATICA' | 'STANDEN' | null
}

/** Het aandeel van één voertuig in de balans van een medewerker. */
export interface KmBalansVoertuigAandeelDTO {
  voertuigId: string
  kenteken: string
  /** Toewijzingsdagen van deze medewerker op dit voertuig (een wisseldag kan half zijn). */
  dagen: number
  /** De dagen van de maand: de noemer van de verdeling (met en zonder bestuurder). */
  dagenVoertuig: number
  /** Het deel van de dagen (en dus van de kilometers) voor deze medewerker. */
  aandeelPct: number | null
  kmVoertuig: number | null
  kmToegerekend: number | null
  bron: KmBalansBron
  zekerheid: KmBalansZekerheid | null
  bandbreedtePct: number | null
  factoren: KmVoertuigFactorGebruikDTO[]
  werkKm: number
  wisseldagen: number
  wisseldagenGelijkVerdeeld: number
  dagenZonderBestuurder: number
  /** Het deel van de dagen zonder bestuurder: pro rata, aan niemand toegerekend (null = geen meting). */
  kmNietToegerekend: number | null
  genegeerdeStanden: number
  begin: KmAnkerDTO | null
  eind: KmAnkerDTO | null
  /** De andere bestuurders van dit voertuig in de maand (naam + dagen). */
  medeBestuurders: Array<{ employeeId: string; naam: string; dagen: number }>
}

export interface KmBalansWerkOpbouwDTO {
  /** Werkkilometers op dagen met een bedrijfsauto met bekend totaal (telt in de vergelijking). */
  werkKm: number
  zonderBedrijfsauto: number
  opVoertuigZonderMeting: number
  /** Woon-werkkilometers die volgens de instelling niet als werk tellen. */
  woonWerkUitgesloten: number
  /** Kilometers van piket-oproepen (consignatie), uit de oproepen zelf. */
  consignatieKm: number
  dagregelsZonderReis: number
  /** Ritten zonder afstand (luchtlijn zonder coördinaten, 0 km) op dagen met een bedrijfsauto. */
  rittenGeschat: number
  dagenMetWerk: number
}

export interface KmBalansOpbouwDTO {
  voertuigen: KmBalansVoertuigAandeelDTO[]
  werk: KmBalansWerkOpbouwDTO
  instellingen: {
    woonWerkTeltAlsWerk: boolean
    drempelPrivePct: number
    drempelPriveKm: number
    minWerkdagen: number
    bandbreedteStandaardPct: number
  }
  verklaringId: string | null
  /** De vergelijkingswaarde: bij een bandbreedte de ondergrens. */
  vergelijkPriveKm: number | null
  vergelijkPrivePct: number | null
}

// ─── Lees-DTO's backoffice ───────────────────────────────────────────────────

export interface KmBalansRijDTO {
  employeeId: string
  naam: string
  personeelsnummer: string | null
  bvNaam: string | null
  periode: number
  periodeLabel: string
  voertuigen: Array<{ voertuigId: string; kenteken: string }>
  /**
   * Bij een gedeeld voertuig (meer bestuurders in de maand): de korte weergave
   * ("gedeeld voertuig: 14 van 30 dagen") en de uitleg van de verdeling (tooltip).
   * Zonder namen; die staan in het detail. Null = niet gedeeld.
   */
  verdeling: { kort: string; uitleg: string } | null
  totaalKm: number | null
  bron: KmBalansBron
  bronLabel: string
  /** Bij een meting: hoe hij tot stand kwam ("lineair tussen kilometerstanden rond de maandgrens"). */
  meetTekst: string | null
  /** De lopende maand: de cijfers gelden tot en met vandaag (werk en totaal). */
  lopend: boolean
  zekerheid: KmBalansZekerheid | null
  bandbreedtePct: number | null
  /** Ondergrens en bovengrens van de privé-indicatie bij een bandbreedte. */
  priveKmMin: number | null
  priveKmMax: number | null
  /** Alle werkkilometers op dagen met een bedrijfsauto, ook als het totaal van het voertuig nog onbekend is. */
  werkKm: number
  /** Het deel van `werkKm` op een voertuig zonder totaal: dat telt pas in de privé-indicatie zodra het totaal bekend is. */
  werkKmZonderMeting: number
  priveKm: number | null
  werkPct: number | null
  privePct: number | null
  /** "1 op 13 (standaardverbruik)"; null bij een meting. */
  factorTekst: string | null
  werkBovenTotaal: boolean
  werkOnvolledig: boolean
  werkdagen: number
  verklaringType: VoertuigGebruikType | null
  /** Boven de ingestelde drempel bij de verklaring "uitsluitend zakelijk". */
  bovenDrempel: boolean
  /** Zoveel maanden op rij boven de drempel als ingesteld (signaal). */
  structureelBovenDrempel: boolean
  markeringen: KmBalansMarkering[]
  status: KmBalansStatus
  afgeslotenOp: string | null
  /** De bron is na het afsluiten gewijzigd: herberekenen geeft een andere uitkomst. */
  bronGewijzigdOp: string | null
  berekendOp: string
}

export interface KmBalansKpiDTO {
  medewerkers: number
  werkBovenTotaal: number
  /** Boven de drempel, ZONDER de maanden met weinig werkdagen (die staan apart). */
  bovenDrempel: number
  /** Boven de drempel in een maand met weinig werkdagen: geen melding, apart geteld. */
  bovenDrempelWeinigWerkdagen: number
  structureelBovenDrempel: number
  geenMeting: number
}

export interface KmBalansOverzichtDTO {
  rijen: KmBalansRijDTO[]
  meta: {
    total: number
    page: number
    pageSize: number
    periode: number
    periodeLabel: string
    kpi: KmBalansKpiDTO
    /** Het standaardverbruik (liter) zoals het nu geldt, voor de uitleg. */
    standaardTekst: string
    magHerberekenen: boolean
    magInstellingenWijzigen: boolean
    disclaimer: string
  }
}

export const KM_BALANS_VERKLARING_FILTERS = ['UITSLUITEND_ZAKELIJK', 'OOK_PRIVE', 'GEEN'] as const
export type KmBalansVerklaringFilter = (typeof KM_BALANS_VERKLARING_FILTERS)[number]

export const KM_BALANS_VERKLARING_FILTER_LABELS: Record<KmBalansVerklaringFilter, string> = {
  UITSLUITEND_ZAKELIJK: 'Uitsluitend zakelijk',
  OOK_PRIVE: 'Ook privé',
  GEEN: 'Geen verklaring',
}

export const kmBalansOverzichtQuerySchema = z.object({
  periode: periodeLabelSchema,
  entityId: idParam('entityId').optional(),
  departmentId: idParam('departmentId').optional(),
  employeeId: idParam('employeeId').optional(),
  verklaring: z.enum(KM_BALANS_VERKLARING_FILTERS).optional(),
  alleenAfwijkingen: queryBoolean().optional(),
  /** Alleen wie het ingestelde aantal maanden op rij boven de drempel ligt (de HR-melding). */
  alleenStructureel: queryBoolean().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type KmBalansOverzichtQuery = z.infer<typeof kmBalansOverzichtQuerySchema>

export interface KmBalansPeriodeDetailDTO extends KmBalansRijDTO {
  opbouw: KmBalansOpbouwDTO | null
}

export interface KmBalansMedewerkerDTO {
  employeeId: string
  naam: string
  van: string
  tot: string
  perioden: KmBalansPeriodeDetailDTO[]
  magHerberekenen: boolean
  disclaimer: string
}

/** Drill-down van één medewerker-maand: de tankbeurten en de dagregels achter de cijfers. */
export interface KmBalansDrilldownDTO {
  employeeId: string
  naam: string
  periode: number
  periodeLabel: string
  dagen: Array<{
    dag: string
    kentekens: string[]
    werkKm: number
    telt: boolean
    reden: 'GEEN_BEDRIJFSAUTO' | 'VOERTUIG_ZONDER_METING' | null
    ritten: Array<{
      routeType: string
      routeLabel: string
      km: number
      telt: boolean
      /** Een rit zonder afstand (luchtlijn zonder coördinaten, 0 km): de werkkilometers zijn dan onvolledig. */
      geschat: boolean
      werkbonNummer: string | null
      klantNaam: string | null
    }>
    consignatieKm: number
    dagregelsZonderReis: number
  }>
  tankbeurten: Array<{
    op: string
    kenteken: string | null
    hoeveelheid: number
    drager: KmEnergieDrager
    kmStand: number | null
    prive: boolean
    bron: 'FACTUUR' | 'TANKPAS'
  }>
  kmStanden: Array<{ op: string; kenteken: string; kmStand: number; bron: string; genegeerd: boolean }>
}

/** Voertuig-detail: per maand de kilometers van het voertuig en de verdeling over de bestuurders. */
export interface KmBalansVoertuigDTO {
  voertuigId: string
  kenteken: string
  perioden: Array<{
    periode: number
    periodeLabel: string
    opbouw: KmVoertuigOpbouwDTO | null
    factorVastgezet: boolean
    bestuurders: Array<{ employeeId: string; naam: string; dagen: number; kmToegerekend: number | null }>
    /** Dagen zonder bestuurder en hun (pro rata) niet toegerekende kilometers; null = geen. */
    nietToegerekend: { dagen: number; km: number | null } | null
    /** Afgesloten maand: de kilometers waarmee verdeeld is (liggen vast). */
    kmBijAfsluiten: number | null
    /** En de bron die die kilometers toen hadden (naast de huidige bron van het voertuig). */
    bronBijAfsluiten: KmBalansBron | null
    /** Het voertuigtotaal is na het afsluiten gewijzigd: de verdeling hoort bij kmBijAfsluiten. */
    gewijzigdNaAfsluiten: boolean
  }>
  disclaimer: string
}

// ─── Herberekenen op verzoek (met rapport) ───────────────────────────────────

/**
 * Zoveel maanden herberekent één verzoek hooguit (het verzoek draait synchroon):
 * voor alle medewerkers, of voor één medewerker.
 */
export const KM_BALANS_HERBEREKEN_MAX_MAANDEN = { alle: 6, medewerker: 13 } as const

const maandIndex = (label: string): number | null => {
  const m = /^(\d{4})-(\d{2})$/.exec(label)
  return m ? Number(m[1]) * 12 + Number(m[2]) - 1 : null
}

export const kmBalansHerberekenSchema = z
  .object({
    van: periodeLabelSchema,
    tot: periodeLabelSchema,
    employeeId: idParam('employeeId').optional(),
    /** Standaard: alleen een rapport, niets opslaan. */
    dryRun: z.boolean().default(true),
    /** Ook bij afgesloten maanden de huidige verbruiksfactoren gebruiken. */
    herbepaalFactor: z.boolean().default(false),
    reden: z.string().trim().max(500, 'Maximaal 500 tekens.').optional(),
  })
  .superRefine((v, ctx) => {
    if (typeof v.van !== 'string' || typeof v.tot !== 'string') return
    if (v.van > v.tot) {
      ctx.addIssue({ code: 'custom', path: ['tot'], message: 'De eindperiode ligt vóór de beginperiode.' })
      return
    }
    const a = maandIndex(v.van)
    const b = maandIndex(v.tot)
    if (a == null || b == null) return
    const max = v.employeeId ? KM_BALANS_HERBEREKEN_MAX_MAANDEN.medewerker : KM_BALANS_HERBEREKEN_MAX_MAANDEN.alle
    if (b - a + 1 > max) {
      ctx.addIssue({
        code: 'custom',
        path: ['van'],
        message: v.employeeId
          ? `Herbereken hooguit ${max} maanden tegelijk.`
          : `Herbereken voor alle medewerkers hooguit ${max} maanden tegelijk (of kies een medewerker).`,
      })
    }
  })
export type KmBalansHerberekenInvoer = z.infer<typeof kmBalansHerberekenSchema>

export interface KmBalansHerberekenRegelDTO {
  employeeId: string
  naam: string
  periode: number
  periodeLabel: string
  afgesloten: boolean
  oud: { totaalKm: number | null; werkKm: number; priveKm: number | null; bron: KmBalansBron } | null
  nieuw: { totaalKm: number | null; werkKm: number; priveKm: number | null; bron: KmBalansBron }
}

export interface KmBalansHerberekenRapportDTO {
  van: string
  tot: string
  dryRun: boolean
  herbepaalFactor: boolean
  aantalPerioden: number
  aantalVoertuigPerioden: number
  aantalGewijzigd: number
  wijzigingen: KmBalansHerberekenRegelDTO[]
  /** Meer wijzigingen dan getoond (het rapport toont er maximaal 500). */
  afgekapt: boolean
}

// ─── Portal (eigen balans, nooit bedragen) ───────────────────────────────────

export interface MijnKmBalansPeriodeDTO {
  periode: number
  periodeLabel: string
  totaalKm: number | null
  bron: KmBalansBron
  bronLabel: string
  bronUitleg: string
  zekerheid: KmBalansZekerheid | null
  zekerheidLabel: string | null
  /** Bij een meting: hoe hij tot stand kwam. */
  meetTekst: string | null
  /** De lopende maand: tot en met vandaag. */
  lopend: boolean
  /** Bij een auto die de bestuurder maar een deel van de maand had: de verdeling (zonder namen). */
  verdeling: string | null
  bandbreedtePct: number | null
  priveKmMin: number | null
  priveKmMax: number | null
  werkKm: number
  priveKm: number | null
  werkPct: number | null
  privePct: number | null
  /** "1 op 13 (standaardverbruik)"; null bij een meting. */
  factorTekst: string | null
  werkOnvolledig: boolean
  /** Werk hoger dan het totaal: dan klopt de schatting, een kilometerstand of een reisberekening niet. */
  werkBovenTotaal: boolean
}

export interface MijnKmBalansDTO {
  perioden: MijnKmBalansPeriodeDTO[]
  laatsteKmStand: { datum: string; kmStand: number } | null
  /** Langer dan het ingestelde aantal maanden geen kilometerstand doorgegeven. */
  geenRecenteKmStand: boolean
  uitleg: string
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface KmBalansDashboardTegelDTO {
  geenToegang: boolean
  periode: number | null
  periodeLabel: string | null
  /** Medewerkers met "uitsluitend zakelijk" van wie de privé-indicatie boven de drempel ligt. */
  bovenDrempel: number
  /** Boven de drempel in een maand met weinig werkdagen (apart, geen melding). */
  bovenDrempelWeinigWerkdagen: number
  structureelBovenDrempel: number
  werkBovenTotaal: number
  medewerkers: number
}
